import React, { useContext, useState } from 'react';

import {
  getAllowedActions,
  getAllowedFilters,
  getRequiredFilters,
} from 'common/automations/automationUtils';
import {
  type Action,
  type ActionType,
  type EditableAutomation,
  type Filter,
  type Resource,
  Trigger,
} from 'common/automations/constants';
import type { ErrorTypes } from 'common/automations/validateAutomation/validateAutomation';
import { BoardsContext } from 'common/containers/BoardsContainer';
import ErrorMessage from 'common/ErrorMessage';
import { FiltersOperator } from 'common/fieldFilters/utils';
import generateRandomID from 'common/generateRandomID';
import { cn } from 'common/style/utils';
import Button from 'common/uiV3/Button';
import Dialog from 'common/uiV3/Dialog';
import Field from 'common/uiV3/Field';
import Flex from 'common/uiV3/Flex';
import HStack from 'common/uiV3/HStack';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';
import { Separator } from 'common/uiV3/Separator';
import VStack from 'common/uiV3/VStack';
import { Info, type LucideIcon, Play, Plus, Zap } from 'lucide-react';
import ActionRow, { type FormAction } from './ActionRow';
import FilterRow, { type FormFilter } from './FilterRow';
import {
  createDefaultAction,
  createDefaultFilter,
  findActiveOption,
  getFirstAction,
  getFirstFilter,
} from './util';

import 'css/components/subdomain/admin/AdminAutomationSettings/_AutomationModal.scss';

export interface FormAutomation extends EditableAutomation {
  actions: FormAction[];
  filters: FormFilter[];
}

const defaultFormValues: FormAutomation = {
  actions: [],
  filters: [],
  filtersOperator: FiltersOperator.all,
  title: '',
  trigger: Trigger.postCreation,
};

const triggerOptions = [
  {
    label: 'Post is created',
    value: Trigger.postCreation,
  },
];

export type FormError = { type: ErrorTypes | 'all'; message: string };

type Props = {
  isEditing: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: FormAutomation) => Promise<FormError | void>;
  defaultValues?: EditableAutomation;
};

const AutomationModal = ({
  isEditing,
  isOpen,
  onClose,
  onSave,
  defaultValues = defaultFormValues,
}: Props) => {
  const boards = useContext(BoardsContext);

  const [formValues, _setFormValues] = useState<FormAutomation>(() => {
    // Add ids to the initial state of actions and filters
    return {
      ...defaultValues,
      actions: defaultValues.actions.map((action) => ({ ...action, id: generateRandomID() })),
      filters: defaultValues.filters.map((filter) => ({ ...filter, id: generateRandomID() })),
    };
  });
  const [error, setError] = useState<FormError | undefined>();

  // Boards could potentially be loading. If so, wait
  if (!Array.isArray(boards)) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const requiredFilterTypes = getRequiredFilters(formValues);
  const allowedFilterTypes = getAllowedFilters(formValues);

  const allowedActionTypes = getAllowedActions(formValues);

  // General helpers
  const setFormValue = <K extends keyof FormAutomation>(valueKey: K, value: FormAutomation[K]) => {
    setError(undefined);
    _setFormValues({ ...formValues, [valueKey]: value });
  };

  const onSaveClicked = async () => {
    const saveError = await onSave(formValues);
    if (saveError) {
      setError(saveError);
    }
  };

  // Filter helpers
  const onCreateFilter = () => {
    const newFilters: FormFilter[] = [
      ...formValues.filters,
      {
        id: generateRandomID(),
        ...createDefaultFilter(getFirstFilter(allowedFilterTypes)),
      },
    ];
    _setFormValues({ ...formValues, filters: newFilters });
  };

  const onRemoveFilter = (id: string) => {
    const newFilters: FormFilter[] = formValues.filters.filter((filter) => filter.id !== id);
    _setFormValues({ ...formValues, filters: newFilters });
  };

  const onResourceChange = (id: string, resource: Resource) => {
    const newFilters = formValues.filters.map((filter) => {
      if (filter.id === id) {
        return {
          id,
          ...createDefaultFilter(resource),
        };
      } else {
        return filter;
      }
    });
    _setFormValues({ ...formValues, filters: newFilters });
  };

  const onSetFilter = <F extends Filter>(
    id: string,
    condition: F['condition'],
    value: F['value'],
  ) => {
    const newFilters = formValues.filters.map((filter) => {
      if (filter.id === id) {
        return {
          ...filter,
          condition,
          value,
        };
      } else {
        return filter;
      }
    });
    _setFormValues({ ...formValues, filters: newFilters });
  };

  // Action helpers
  const onCreateAction = () => {
    const newActions: FormAction[] = [
      ...formValues.actions,
      {
        id: generateRandomID(),
        ...createDefaultAction(getFirstAction(allowedActionTypes)),
      },
    ];

    const requiredFilterTypes = getRequiredFilters({ ...formValues, actions: newActions });
    const missingRequiredFilters = requiredFilterTypes.filter((newFilterType) =>
      formValues.filters.every((filter) => filter.resource !== newFilterType),
    );

    const newFilters: FormFilter[] = [
      ...formValues.filters,
      ...missingRequiredFilters.map((filterType) => ({
        id: generateRandomID(),
        ...createDefaultFilter(filterType),
      })),
    ];

    _setFormValues({ ...formValues, actions: newActions, filters: newFilters });
  };

  const onActionTypeChange = (id: string, actionType: ActionType) => {
    const newActions: FormAction[] = formValues.actions.map((action) => {
      if (action.id === id) {
        return {
          id,
          ...createDefaultAction(actionType),
        };
      } else {
        return action;
      }
    });

    const requiredFilterTypes = getRequiredFilters({ ...formValues, actions: newActions });
    const missingRequiredFilters = requiredFilterTypes.filter((newFilterType) =>
      formValues.filters.every((filter) => filter.resource !== newFilterType),
    );

    const newFilters: FormFilter[] = [
      ...formValues.filters,
      ...missingRequiredFilters.map((filterType) => ({
        id: generateRandomID(),
        ...createDefaultFilter(filterType),
      })),
    ];

    _setFormValues({ ...formValues, actions: newActions, filters: newFilters });
  };

  const onSetAction = <A extends Action>(id: string, properties: A['properties']) => {
    const newActions = formValues.actions.map((action) => {
      if (action.id === id) {
        return {
          ...action,
          properties,
        };
      } else {
        return action;
      }
    });
    _setFormValues({ ...formValues, actions: newActions });
  };

  const onRemoveAction = (id: string) => {
    const newActions: FormAction[] = formValues.actions.filter((action) => action.id !== id);
    _setFormValues({ ...formValues, actions: newActions });
  };

  const resetForm = () => {
    _setFormValues(defaultFormValues);
  };

  const requiredFilters = formValues.filters.filter((filter) =>
    requiredFilterTypes.includes(filter.resource),
  );
  const optionalFilters = formValues.filters.filter(
    (filter) => !requiredFilterTypes.includes(filter.resource),
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content className="sm:max-w-[660px]" onCloseAutoFocus={resetForm}>
        <Dialog.Header>
          <Dialog.Title>{isEditing ? 'Edit automation' : 'Create new automation'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className="px-0 gap-0">
          <VStack className="px-4 pb-4 gap-4">
            <AutomationStep
              title="Overview"
              description="Assign a label to the automation."
              icon={Info}
              iconClassName="stroke-white"
            />
            <Field>
              <Field.Label required>Title</Field.Label>
              <Field.Content>
                <Input
                  id="automationTitle"
                  onChange={(value) => setFormValue('title', value)}
                  placeholder="Enter a title"
                  value={formValues.title}
                />
              </Field.Content>
              {(error?.type === 'title' || error?.type === 'trigger') && (
                <Field.Error>{error.message}</Field.Error>
              )}
            </Field>
          </VStack>
          <Separator />
          <VStack className="p-4 gap-4">
            <AutomationStep
              title="Conditions"
              description="The automation triggers when the following occurs."
              icon={Play}
            />
            <Field>
              <Field.Label required>When</Field.Label>
              <Field.Content>
                <Select
                  onValueChange={(option) => setFormValue('trigger', option as Trigger)}
                  options={triggerOptions}
                  placeholder="Select a trigger"
                  value={findActiveOption(triggerOptions, formValues.trigger)?.value}
                />
              </Field.Content>
            </Field>
            {requiredFilters.length > 0 || optionalFilters.length > 0 ? (
              <VStack className="gap-2">
                {formValues.filters.length > 0 ? <p>and all of these conditions are met</p> : null}
                <VStack className="gap-2">
                  {requiredFilters.map((filter) => {
                    return (
                      <FilterRow
                        allowedFilters={allowedFilterTypes}
                        key={filter.id}
                        filter={filter}
                        filters={formValues.filters}
                        onResourceChange={onResourceChange}
                        onRemove={onRemoveFilter}
                        onFilterChange={(condition, value) =>
                          onSetFilter(filter.id, condition, value)
                        }
                        required
                      />
                    );
                  })}
                  {optionalFilters.map((filter) => {
                    return (
                      <FilterRow
                        allowedFilters={allowedFilterTypes}
                        key={filter.id}
                        filter={filter}
                        filters={formValues.filters}
                        onResourceChange={onResourceChange}
                        onRemove={onRemoveFilter}
                        onFilterChange={(condition, value) =>
                          onSetFilter(filter.id, condition, value)
                        }
                        required={false}
                      />
                    );
                  })}
                </VStack>
              </VStack>
            ) : null}
            {allowedFilterTypes.length > 0 ? (
              <Button variant="plain" size="small" onClick={onCreateFilter} className="self-start">
                <Plus /> Add new condition
              </Button>
            ) : null}
            {error?.type === 'filter' && <ErrorMessage>{error.message}</ErrorMessage>}
          </VStack>
          <Separator />
          <VStack className="px-4 pt-4 gap-4">
            <AutomationStep
              title="Outcomes"
              description="The following action(s) will execute as a result."
              icon={Zap}
            />
            <p>Then</p>
            {formValues.actions.length > 0 ? (
              <VStack className="gap-2">
                {formValues.actions.map((action) => {
                  return (
                    <ActionRow
                      key={action.id}
                      allowedActions={allowedActionTypes}
                      action={action}
                      filters={formValues.filters}
                      onActionTypeChange={onActionTypeChange}
                      onRemove={onRemoveAction}
                      onActionChange={(properties) => onSetAction(action.id, properties)}
                    />
                  );
                })}
              </VStack>
            ) : null}
            {allowedActionTypes.length > 0 ? (
              <Button variant="plain" size="small" className="self-start" onClick={onCreateAction}>
                <Plus /> Add new outcome
              </Button>
            ) : null}
            {(error?.type === 'action' || error?.type === 'all') && (
              <ErrorMessage>{error.message}</ErrorMessage>
            )}
          </VStack>
        </Dialog.Body>
        <Dialog.Footer>
          <Button onClick={onSaveClicked}>{isEditing ? 'Save changes' : 'Create rule'}</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

const AutomationStep = ({
  title,
  description,
  icon: Icon,
  iconClassName,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  iconClassName?: string;
}) => {
  return (
    <HStack className="items-center gap-4">
      <Flex className="items-center justify-center base-border size-10 rounded-sm bg-card">
        <Icon size={20} className={cn('fill-primary stroke-primary', iconClassName)} />
      </Flex>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-secondary-foreground">{description}</p>
      </div>
    </HStack>
  );
};

export default AutomationModal;
